import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react'
import { apiFetch, type ApiError } from '../api/client'

export type PlatformRole = 'super_admin' | 'admin' | 'support' | 'analyst'

export interface AdminIdentity {
  is_platform_admin: true
  user_id: string
  // The caller's WORKSPACE role — not the platform tier.
  role: string
  // The caller's platform_admins row (null when there is no row or the server's read
  // failed). UI convenience only: tiered routes re-check the row server-side.
  platform_role: PlatformRole | null
  platform_admin_active: boolean | null
}

// The four states the bootstrap gate can be in.
// 'loading'        — whoami call in flight; render nothing data-bearing.
// 'unauthenticated'— whoami returned 401; show login view.
// 'forbidden'      — whoami returned 403 (or a later /admin/* call did); show not-authorized.
// 'authenticated'  — whoami returned 200 with a platform-admin identity; show shell.
export type AdminStatus = 'loading' | 'unauthenticated' | 'forbidden' | 'authenticated'

interface AdminState {
  status: AdminStatus
  identity: AdminIdentity | null
  // Re-run whoami (e.g. after a successful login). Resets to 'loading' first.
  recheck: () => void
  // Quietly re-read whoami and update identity in place (no 'loading' flash).
  // Used after a self-targeted admin write so the UI tier gate follows a
  // self-demotion immediately. The server stays authoritative either way.
  refreshIdentity: () => Promise<void>
  // Screens call this when they get a 403 from any /admin/* call so it surfaces
  // as the not-authorized view rather than a silent broken state.
  notifyForbidden: () => void
}

const AdminContext = createContext<AdminState | null>(null)

export function AdminProvider({ children }: { children: ReactNode }) {
  // Default 'loading' so the gate blocks before the first whoami resolves.
  const [status, setStatus] = useState<AdminStatus>('loading')
  const [identity, setIdentity] = useState<AdminIdentity | null>(null)
  // Incremented to re-trigger the whoami effect when recheck() is called.
  const [trigger, setTrigger] = useState(0)

  useEffect(() => {
    // `cancelled` prevents stale state updates if the component unmounts or
    // recheck() fires before this fetch completes (cleanup sets it true).
    let cancelled = false
    apiFetch<AdminIdentity>('/admin/whoami')
      .then((data) => {
        if (!cancelled) {
          setIdentity(data)
          setStatus('authenticated')
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const apiErr = err as ApiError
          // 403 = authenticated but not a platform admin.
          // Anything else (401, network error) = no valid session.
          setStatus(apiErr.status === 403 ? 'forbidden' : 'unauthenticated')
        }
      })
    return () => {
      cancelled = true
    }
  }, [trigger])

  // recheck() is called after login to re-run whoami.
  // Sets 'loading' synchronously (not in an effect) then increments the trigger
  // so the effect above fires a fresh fetch.
  const recheck = useCallback(() => {
    setStatus('loading')
    setIdentity(null)
    setTrigger((v) => v + 1)
  }, [])

  const refreshIdentity = useCallback(async () => {
    try {
      const data = await apiFetch<AdminIdentity>('/admin/whoami')
      setIdentity(data)
    } catch (err: unknown) {
      const apiErr = err as ApiError
      if (apiErr.status === 403) setStatus('forbidden')
      else if (apiErr.status === 401) setStatus('unauthenticated')
      // Other errors: keep the current identity; the server still enforces every write.
    }
  }, [])

  const notifyForbidden = useCallback(() => {
    setStatus('forbidden')
  }, [])

  return (
    <AdminContext.Provider value={{ status, identity, recheck, refreshIdentity, notifyForbidden }}>
      {children}
    </AdminContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAdminState(): AdminState {
  const ctx = useContext(AdminContext)
  if (!ctx) throw new Error('useAdminState must be used within AdminProvider')
  return ctx
}
